// Seller Registration Translations

export const SELLER_TRANSLATIONS = {
  en: {
    pageTitle: 'Seller Registration',
    steps: {
      name: 'Step',
      of: 'of',
      account: 'Account',
      profile: 'Profile',
      terms: 'Terms',
    },
    stepsFlow: 'Steps 1 → 3: Account → Company Profile → Terms and Conditions',
    alreadyMember: 'Already a member?',
    login: 'Login',
    
    // Navigation
    nav: {
      back: '← Back',
      continue: 'Continue →',
      submit: 'Submit',
      submitting: 'Submitting...',
    },
    
    // Success screen
    success: {
      title: 'Profile Created Successfully!',
      message: 'Congratulations! Your seller profile has been created successfully. A confirmation email has been sent to the address you provided.',
      emailConfirmation: 'Please check your inbox and confirm your email address to access your dashboard.',
      goToLogin: 'Go to Homepage',
    },
    
    // Step 1: Account & Contact
    step1: {
      sectionTitle: 'Account Information',
      firstName: 'First Name *',
      firstNamePlaceholder: 'John',
      lastName: 'Last Name *',
      lastNamePlaceholder: 'Doe',
      email: 'Email *',
      emailPlaceholder: 'john.doe@example.com',
      phone: 'Phone *',
      phonePlaceholder: '+1 (514) 123-4567',
      password: 'Password *',
      passwordPlaceholder: '••••••••',
      confirmPassword: 'Confirm Password *',
      confirmPasswordPlaceholder: '••••••••',
      howHeard: 'How did you hear about AcquizGroup? *',
      howHeardPlaceholder: 'Select an option',
    },
    
    // Step 2: Company Profile
    step2: {
      sectionTitle: 'Company Information',
      companyName: 'Company Name *',
      companyNamePlaceholder: 'ABC Company Inc.',
      industry: 'Industry Sector *',
      industryPlaceholder: 'Select a sector',
      yearEstablished: 'Year Established *',
      yearEstablishedPlaceholder: 'YYYY',
      yearEstablishedHint: 'Company must have at least 3 years of history (before 2022)',
      
      // Jurisdictions
      jurisdictionsTitle: 'Operating Jurisdictions',
      jurisdictionsCanada: 'Canada',
      jurisdictionsUSA: 'United States',
      jurisdictionsEurope: 'Europe',
      jurisdictionsAustralia: 'Australia',
      jurisdictionsHint: 'Select all regions where your company operates',
      
      // Address
      addressSection: 'Company Address',
      addressSearch: 'Address Search',
      addressSearchPlaceholder: 'Start typing your address...',
      addressSearchHint: 'Select a suggestion to auto-fill the fields below',
      companyStreet: 'Street Address *',
      companyStreetPlaceholder: '123 Main Street',
      companyCity: 'City *',
      companyCityPlaceholder: 'Montreal',
      companyProvince: 'Province/State *',
      companyProvincePlaceholder: 'Select',
      companyZipCode: 'Postal Code *',
      companyZipCodePlaceholder: 'H1A 1A1',
      
      // Financials
      financialsSection: 'Financial Information',
      annualRevenue: 'Annual Gross Revenue *',
      annualRevenuePlaceholder: 'Select a range',
      ebitda: 'EBITDA *',
      ebitdaPlaceholder: 'Select a range',
      askingPrice: 'Asking Price *',
      askingPricePlaceholder: 'Select a range',
      positiveCashFlow: 'Positive Cash Flow? *',
      positiveCashFlowYes: 'Yes',
      positiveCashFlowNo: 'No',
      
      // Documents
      documentsSection: 'Required Documents',
      documentsHint: 'Check all documents you can provide',
      doc3YearsPL: '3 years of P&L statements',
      doc3YearsTax: '3 years of tax reports',
      docRecentBalance: 'Recent balance sheet',
      documentsReady: 'Are documents ready for review? *',
      documentsReadyYes: 'Yes, ready to share',
      documentsReadyNo: 'Not yet, need time to prepare',
      
      // Additional info
      firstSale: 'Is this your first business sale? *',
      firstSaleYes: 'Yes, first time',
      firstSaleNo: 'No, I have sold businesses before',
      needGuidance: 'Do you need guidance through the process? *',
      needGuidanceYes: 'Yes, I would like assistance',
      needGuidanceNo: 'No, I am familiar with the process',
    },
    
    // Step 3: Terms and Conditions
    step3: {
      contractTitle: 'SERVICE AND CONFIDENTIALITY AGREEMENT',
      article1Title: 'Article 1. Purpose of the Agreement',
      article1Content: 'AcquizGroup undertakes to provide a matchmaking platform and transaction management tools to the Seller. The Seller agrees to use the platform in good faith and in compliance with applicable laws and regulations.',
      article2Title: 'Article 2. Confidentiality',
      article2Content: 'All information and documents shared via the platform are strictly confidential. AcquizGroup agrees to protect the Seller\'s business information and only share it with qualified, NDA-bound buyers.',
      article3Title: 'Article 3. Service Fees',
      article3Content: 'The Seller agrees to pay the applicable service fees as outlined in the pricing section. Fees cover platform access, listing services, and transaction support.',
      article4Title: 'Article 4. Listing Accuracy',
      article4Content: 'The Seller certifies that all information provided about their business is accurate and complete to the best of their knowledge.',
      article5Title: 'Article 5. Limitation of Liability',
      article5Content: 'AcquizGroup acts as an intermediary and does not guarantee the successful sale of the business. The Seller remains responsible for all negotiations and final decisions.',
      termsAccept: 'I have read and accept the Terms and Conditions and the Privacy Policy of AcquizGroup.',
      termsError: 'You must accept the Terms and Conditions to continue.',
    },
    
    // Validation errors
    errors: {
      firstNameRequired: 'Please enter your first name.',
      lastNameRequired: 'Please enter your last name.',
      emailRequired: 'Please enter a valid email.',
      emailInvalid: 'Invalid email format.',
      phoneRequired: 'Please enter a phone number.',
      passwordRequired: 'Please choose a password.',
      passwordMinLength: 'Password must be at least 6 characters.',
      confirmPasswordRequired: 'Please confirm your password.',
      passwordMismatch: 'Passwords do not match.',
      howHeardRequired: 'This field is required.',
      companyNameRequired: 'Please enter the company name.',
      industryRequired: 'Please select a sector.',
      yearEstablishedRequired: 'Please enter the year established.',
      yearEstablishedInvalid: 'Year must be before 2022 (3+ years of history required).',
      addressRequired: 'Please enter the address.',
      cityRequired: 'City is required.',
      provinceRequired: 'Province is required.',
      zipCodeRequired: 'Postal code is required.',
      annualRevenueRequired: 'Please enter the gross revenue.',
      ebitdaRequired: 'Please enter the EBITDA.',
      askingPriceRequired: 'Please enter the asking price.',
      positiveCashFlowRequired: 'Please select an option.',
      positiveCashFlowNo: 'Unfortunately, companies without positive cash flow cannot be accepted at this time.',
      documentsRequired: 'You must provide either (3 years of P&L statements + Recent balance sheet) or (3 years of tax reports).',
      documentsReadyRequired: 'Please select an option.',
      firstSaleRequired: 'Please select an option.',
      needGuidanceRequired: 'Please select an option.',
    },
    
    // Marketing sources
       marketingSources: [
      'Facebook',
      'LinkedIn',
      'Instagram',
      'Networking Group',
      'YouTube',
      'Google Search',
      'Friends',
      'Family',
      'Broker',
      'Other',
    ],
    
    // Industries
    industries: [
      'Agriculture, Agri-Food & Food Industry',
      'Automotive & Boat / Marine',
      'Beauty & Personal Care',
      'Retail (General, Sports, Specialized)',
      'Communication, Media & Public Relations',
      'Engineering & Technical Consulting',
      'Construction & Building (incl. Trades & HVAC)',
      'Entertainment, Leisure & Sports',
      'Education & Children Services',
      'Online & Technology (SaaS, Web Dev, IT Services)',
      'Service Businesses (General)',
      'Manufacturing & Industrial Production',
      'Accounting & Tax Firm (CPA, CA)',
      'Legal Firm (Corporate, IP, Real Estate)',
      'Waste Management, Water Treatment & Environment',
      'Wholesalers & Distributors',
      'Business Intelligence & Data Consulting',
      'Unclassifiable / Other',
      'Recruitment & Human Resources',
      'Restaurants & Food Services',
      'Home Services (Residential & Commercial Contractors)',
      'Financial Services & Insurance',
      'Real Estate Services & Property Management',
      'Pet Services',
      'Healthcare, Medical & Life Sciences',
      'Transportation, Trucking & Logistics',
      'Travel, Tourism & Hospitality',
    ],
    
    // Revenue ranges
    revenueRanges: [
      { value: '0-500k', label: 'Under $500k' },
      { value: '500k-1M', label: '$500k - $1M' },
      { value: '1M-2M', label: '$1M - $2M' },
      { value: '2M-5M', label: '$2M - $5M' },
      { value: '5M-10M', label: '$5M - $10M' },
      { value: '10M-25M', label: '$10M - $25M' },
      { value: '25M+', label: '$25M+' },
    ],
    
    // EBITDA ranges
    ebitdaRanges: [
      { value: 'negative', label: 'Negative' },
      { value: '0-100k', label: '$0 - $100k' },
      { value: '100k-250k', label: '$100k - $250k' },
      { value: '250k-500k', label: '$250k - $500k' },
      { value: '500k-1M', label: '$500k - $1M' },
      { value: '1M-2M', label: '$1M - $2M' },
      { value: '2M+', label: '$2M+' },
    ],
    
    // Price ranges
    priceRanges: [
      { value: '0-500k', label: 'Under $500k' },
      { value: '500k-1M', label: '$500k - $1M' },
      { value: '1M-2M', label: '$1M - $2M' },
      { value: '2M-5M', label: '$2M - $5M' },
      { value: '5M-10M', label: '$5M - $10M' },
      { value: '10M-25M', label: '$10M - $25M' },
      { value: '25M-50M', label: '$25M - $50M' },
      { value: '50M+', label: '$50M+' },
    ],
    
    // Jurisdictions Canada
    jurisdictionsCanadaList: [
      'All provinces',
      'Alberta (AB)',
      'British Columbia (BC)',
      'Manitoba (MB)',
      'New Brunswick (NB)',
      'Newfoundland and Labrador (NL)',
      'Northwest Territories (NT)',
      'Nova Scotia (NS)',
      'Nunavut (NU)',
      'Ontario (ON)',
      'Prince Edward Island (PE)',
      'Quebec (QC)',
      'Saskatchewan (SK)',
      'Yukon (YT)',
    ],
    
    // Jurisdictions USA
    jurisdictionsUSAList: [
      'All states',
      'Alabama (AL)',
      'Alaska (AK)',
      'Arizona (AZ)',
      'Arkansas (AR)',
      'California (CA)',
      'Colorado (CO)',
      'Connecticut (CT)',
      'Delaware (DE)',
      'Florida (FL)',
      'Georgia (GA)',
      'Hawaii (HI)',
      'Idaho (ID)',
      'Illinois (IL)',
      'Indiana (IN)',
      'Iowa (IA)',
      'Kansas (KS)',
      'Kentucky (KY)',
      'Louisiana (LA)',
      'Maine (ME)',
      'Maryland (MD)',
      'Massachusetts (MA)',
      'Michigan (MI)',
      'Minnesota (MN)',
      'Mississippi (MS)',
      'Missouri (MO)',
      'Montana (MT)',
      'Nebraska (NE)',
      'Nevada (NV)',
      'New Hampshire (NH)',
      'New Jersey (NJ)',
      'New Mexico (NM)',
      'New York (NY)',
      'North Carolina (NC)',
      'North Dakota (ND)',
      'Ohio (OH)',
      'Oklahoma (OK)',
      'Oregon (OR)',
      'Pennsylvania (PA)',
      'Rhode Island (RI)',
      'South Carolina (SC)',
      'South Dakota (SD)',
      'Tennessee (TN)',
      'Texas (TX)',
      'Utah (UT)',
      'Vermont (VT)',
      'Virginia (VA)',
      'Washington (WA)',
      'West Virginia (WV)',
      'Wisconsin (WI)',
      'Wyoming (WY)',
    ],
    
    // Jurisdictions Europe
    jurisdictionsEuropeList: [
      'All countries',
      'France',
      'Germany',
      'United Kingdom',
      'Switzerland',
      'Belgium',
      'Other',
    ],
    
    // Jurisdictions Australia
    jurisdictionsAustraliaList: [
      'All states',
      'New South Wales',
      'Victoria',
      'Queensland',
      'Other',
    ],
  },
  
  fr: {
    pageTitle: 'Inscription Vendeur',
    steps: {
      name: 'Étape',
      of: 'sur',
      account: 'Compte',
      profile: 'Profil',
      terms: 'Termes',
    },
    stepsFlow: 'Étapes 1 → 3 : Compte → Profil d\'entreprise → Termes et Conditions',
    alreadyMember: 'Déjà membre ?',
    login: 'Connexion',
    
    // Navigation
    nav: {
      back: '← Retour',
      continue: 'Continuer →',
      submit: 'Soumettre',
      submitting: 'Soumission...',
    },
    
    // Success screen
    success: {
      title: 'Profil créé avec succès !',
      message: 'Félicitations ! Votre profil vendeur a été créé avec succès. Un courriel de confirmation vient d\'être envoyé à l\'adresse que vous avez fournie.',
      emailConfirmation: 'Veuillez consulter votre boîte de réception et confirmer votre adresse courriel pour accéder à votre tableau de bord.',
      goToLogin: 'Aller à l\'accueil',
    },
    
    // Step 1: Account & Contact
    step1: {
      sectionTitle: 'Informations du compte',
      firstName: 'Prénom *',
      firstNamePlaceholder: 'Jean',
      lastName: 'Nom de famille *',
      lastNamePlaceholder: 'Dupont',
      email: 'Courriel *',
      emailPlaceholder: 'jean.dupont@exemple.com',
      phone: 'Téléphone *',
      phonePlaceholder: '+1 (514) 123-4567',
      password: 'Mot de passe *',
      passwordPlaceholder: '••••••••',
      confirmPassword: 'Confirmer le mot de passe *',
      confirmPasswordPlaceholder: '••••••••',
      howHeard: 'Comment avez-vous entendu parler d\'AcquizGroup ? *',
      howHeardPlaceholder: 'Sélectionnez une option',
    },
    
    // Step 2: Company Profile
    step2: {
      sectionTitle: 'Informations de l\'entreprise',
      companyName: 'Nom de l\'entreprise *',
      companyNamePlaceholder: 'ABC Compagnie Inc.',
      industry: 'Secteur d\'activité *',
      industryPlaceholder: 'Sélectionnez un secteur',
      yearEstablished: 'Année d\'établissement *',
      yearEstablishedPlaceholder: 'AAAA',
      yearEstablishedHint: 'L\'entreprise doit avoir au moins 3 ans d\'historique (avant 2022)',
      
      // Jurisdictions
      jurisdictionsTitle: 'Juridictions d\'opération',
      jurisdictionsCanada: 'Canada',
      jurisdictionsUSA: 'États-Unis',
      jurisdictionsEurope: 'Europe',
      jurisdictionsAustralia: 'Australie',
      jurisdictionsHint: 'Sélectionnez toutes les régions où votre entreprise opère',
      
      // Address
      addressSection: 'Adresse de l\'entreprise',
      addressSearch: 'Recherche d\'adresse',
      addressSearchPlaceholder: 'Commencez à taper votre adresse...',
      addressSearchHint: 'Sélectionnez une suggestion pour remplir automatiquement les champs ci-dessous',
      companyStreet: 'Adresse de rue *',
      companyStreetPlaceholder: '123 Rue Principale',
      companyCity: 'Ville *',
      companyCityPlaceholder: 'Montréal',
      companyProvince: 'Province/État *',
      companyProvincePlaceholder: 'Sélectionnez',
      companyZipCode: 'Code postal *',
      companyZipCodePlaceholder: 'H1A 1A1',
      
      // Financials
      financialsSection: 'Informations financières',
      annualRevenue: 'Revenu brut annuel *',
      annualRevenuePlaceholder: 'Sélectionnez une fourchette',
      ebitda: 'BAIIDA (EBITDA) *',
      ebitdaPlaceholder: 'Sélectionnez une fourchette',
      askingPrice: 'Prix demandé *',
      askingPricePlaceholder: 'Sélectionnez une fourchette',
      positiveCashFlow: 'Flux de trésorerie positif ? *',
      positiveCashFlowYes: 'Oui',
      positiveCashFlowNo: 'Non',
      
      // Documents
      documentsSection: 'Documents requis',
      documentsHint: 'Cochez tous les documents que vous pouvez fournir',
      doc3YearsPL: '3 ans d\'états des résultats (P&L)',
      doc3YearsTax: '3 ans de déclarations fiscales',
      docRecentBalance: 'Bilan récent',
      documentsReady: 'Les documents sont-ils prêts pour examen ? *',
      documentsReadyYes: 'Oui, prêts à partager',
      documentsReadyNo: 'Pas encore, j\'ai besoin de temps pour préparer',
      
      // Additional info
      firstSale: 'Est-ce votre première vente d\'entreprise ? *',
      firstSaleYes: 'Oui, première fois',
      firstSaleNo: 'Non, j\'ai déjà vendu des entreprises',
      needGuidance: 'Avez-vous besoin d\'accompagnement ? *',
      needGuidanceYes: 'Oui, j\'aimerais de l\'assistance',
      needGuidanceNo: 'Non, je connais le processus',
    },
    
    // Step 3: Terms and Conditions
    step3: {
      contractTitle: 'CONTRAT DE SERVICE ET CONFIDENTIALITÉ',
      article1Title: 'Article 1. Objet du Contrat',
      article1Content: 'AcquizGroup s\'engage à fournir une plateforme de matchmaking et des outils de gestion de transaction au Vendeur. Le Vendeur s\'engage à utiliser la plateforme de bonne foi et conformément aux lois et règlements en vigueur.',
      article2Title: 'Article 2. Confidentialité',
      article2Content: 'Toutes les informations et documents partagés via la plateforme sont strictement confidentiels. AcquizGroup s\'engage à protéger les informations commerciales du Vendeur et à ne les partager qu\'avec des acheteurs qualifiés ayant signé une NDA.',
      article3Title: 'Article 3. Frais de Service',
      article3Content: 'Le Vendeur accepte de payer les frais de service applicables tels que décrits dans la section tarification. Les frais couvrent l\'accès à la plateforme, les services de listing et le support transactionnel.',
      article4Title: 'Article 4. Exactitude du Listing',
      article4Content: 'Le Vendeur certifie que toutes les informations fournies concernant son entreprise sont exactes et complètes au meilleur de sa connaissance.',
      article5Title: 'Article 5. Limitation de Responsabilité',
      article5Content: 'AcquizGroup agit en tant qu\'intermédiaire et ne garantit pas la vente réussie de l\'entreprise. Le Vendeur reste responsable de toutes les négociations et décisions finales.',
      termsAccept: 'J\'ai lu et j\'accepte les Termes et Conditions ainsi que la Politique de Confidentialité d\'AcquizGroup.',
      termsError: 'Vous devez accepter les Termes et Conditions pour continuer.',
    },
    
    // Validation errors
    errors: {
      firstNameRequired: 'Veuillez entrer votre prénom.',
      lastNameRequired: 'Veuillez entrer votre nom de famille.',
      emailRequired: 'Veuillez entrer un courriel valide.',
      emailInvalid: 'Courriel invalide.',
      phoneRequired: 'Veuillez entrer un numéro de téléphone.',
      passwordRequired: 'Veuillez choisir un mot de passe.',
      passwordMinLength: 'Le mot de passe doit contenir au moins 6 caractères.',
      confirmPasswordRequired: 'Veuillez confirmer votre mot de passe.',
      passwordMismatch: 'Les mots de passe ne correspondent pas.',
      howHeardRequired: 'Ce champ est obligatoire.',
      companyNameRequired: 'Veuillez entrer le nom de la compagnie.',
      industryRequired: 'Veuillez sélectionner un secteur.',
      yearEstablishedRequired: 'Veuillez entrer l\'année d\'établissement.',
      yearEstablishedInvalid: 'L\'année doit être antérieure à 2022 (plus de 3 ans d\'historique).',
      addressRequired: 'Veuillez entrer l\'adresse.',
      cityRequired: 'Ville requise.',
      provinceRequired: 'Province requise.',
      zipCodeRequired: 'Code postal requis.',
      annualRevenueRequired: 'Veuillez entrer le revenu brut.',
      ebitdaRequired: 'Veuillez entrer l\'EBITDA.',
      askingPriceRequired: 'Veuillez entrer le prix demandé.',
      positiveCashFlowRequired: 'Veuillez sélectionner une option.',
      positiveCashFlowNo: 'Malheureusement, les entreprises sans flux de trésorerie positif ne peuvent pas être acceptées pour le moment.',
      documentsRequired: 'Vous devez fournir soit (3 ans d\'états des résultats + Bilan récent) soit (3 ans de déclarations fiscales).',
      documentsReadyRequired: 'Veuillez sélectionner une option.',
      firstSaleRequired: 'Veuillez sélectionner une option.',
      needGuidanceRequired: 'Veuillez sélectionner une option.',
    },
    
    // Marketing sources
       marketingSources: [
      'Facebook',
      'LinkedIn',
      'Instagram',
      'Groupe de réseautage',
      'Youtube',
      'Recherches Google',
      'Amis',
      'Famille',
      'Courtier',
      'Autre',
    ],
    
    // Industries
    industries: [
      'Agriculture, Agro-alimentaire & Industrie alimentaire',
      'Automobile & Bateau / Marine',
      'Beauté & Soins personnels',
      'Commerce de détail (Général, Sport, Spécialisé)',
      'Communication, Médias & Relations publiques',
      'Conseil en génie & Technique (Civil, Électrique, Chimique, Aéro)',
      'Construction & Bâtiment (incl. Métiers & CVC)',
      'Divertissement, Loisirs & Sport',
      'Éducation & Services aux enfants',
      'En ligne & Technologie (SaaS, Dév. Web, Services TI)',
      'Entreprises de services (Général)',
      'Fabrication & Production industrielle',
      'Firme comptable & Fiscale (CPA, CA)',
      'Firme juridique (Corporatif, PI, Immobilier)',
      'Gestion des déchets, Traitement de l\'eau & Environnement',
      'Grossistes & Distributeurs',
      'Intelligence d\'affaires & Conseil en données',
      'Non classifiable / Autre',
      'Recrutement & Ressources humaines',
      'Restaurants & Services alimentaires',
      'Services à domicile (Contracteurs Résidentiels & Commerciaux)',
      'Services financiers & Assurance',
      'Services immobiliers & Gestion de propriétés',
      'Services pour animaux',
      'Soins de santé, Médical & Sciences de la vie',
      'Transport, Camionnage & Logistique',
      'Voyage, Tourisme & Hôtellerie',
    ],
    
    // Revenue ranges
    revenueRanges: [
      { value: '0-500k', label: 'Moins de 500k $' },
      { value: '500k-1M', label: '500k $ - 1M $' },
      { value: '1M-2M', label: '1M $ - 2M $' },
      { value: '2M-5M', label: '2M $ - 5M $' },
      { value: '5M-10M', label: '5M $ - 10M $' },
      { value: '10M-25M', label: '10M $ - 25M $' },
      { value: '25M+', label: '25M $ +' },
    ],
    
    // EBITDA ranges
    ebitdaRanges: [
      { value: 'negative', label: 'Négatif' },
      { value: '0-100k', label: '0 $ - 100k $' },
      { value: '100k-250k', label: '100k $ - 250k $' },
      { value: '250k-500k', label: '250k $ - 500k $' },
      { value: '500k-1M', label: '500k $ - 1M $' },
      { value: '1M-2M', label: '1M $ - 2M $' },
      { value: '2M+', label: '2M $ +' },
    ],
    
    // Price ranges
    priceRanges: [
      { value: '0-500k', label: 'Moins de 500k $' },
      { value: '500k-1M', label: '500k $ - 1M $' },
      { value: '1M-2M', label: '1M $ - 2M $' },
      { value: '2M-5M', label: '2M $ - 5M $' },
      { value: '5M-10M', label: '5M $ - 10M $' },
      { value: '10M-25M', label: '10M $ - 25M $' },
      { value: '25M-50M', label: '25M $ - 50M $' },
      { value: '50M+', label: '50M $ +' },
    ],
    
    // Jurisdictions Canada
    jurisdictionsCanadaList: [
      'Toutes les provinces',
      'Alberta (AB)',
      'Colombie-Britannique (BC)',
      'Manitoba (MB)',
      'Nouveau-Brunswick (NB)',
      'Terre-Neuve-et-Labrador (NL)',
      'Territoires du Nord-Ouest (NT)',
      'Nouvelle-Écosse (NS)',
      'Nunavut (NU)',
      'Ontario (ON)',
      'Île-du-Prince-Édouard (PE)',
      'Québec (QC)',
      'Saskatchewan (SK)',
      'Yukon (YT)',
    ],
    
    // Jurisdictions USA
    jurisdictionsUSAList: [
      'Tous les états',
      'Alabama (AL)',
      'Alaska (AK)',
      'Arizona (AZ)',
      'Arkansas (AR)',
      'Californie (CA)',
      'Colorado (CO)',
      'Connecticut (CT)',
      'Delaware (DE)',
      'Floride (FL)',
      'Géorgie (GA)',
      'Hawaï (HI)',
      'Idaho (ID)',
      'Illinois (IL)',
      'Indiana (IN)',
      'Iowa (IA)',
      'Kansas (KS)',
      'Kentucky (KY)',
      'Louisiane (LA)',
      'Maine (ME)',
      'Maryland (MD)',
      'Massachusetts (MA)',
      'Michigan (MI)',
      'Minnesota (MN)',
      'Mississippi (MS)',
      'Missouri (MO)',
      'Montana (MT)',
      'Nebraska (NE)',
      'Nevada (NV)',
      'New Hampshire (NH)',
      'New Jersey (NJ)',
      'Nouveau-Mexique (NM)',
      'New York (NY)',
      'Caroline du Nord (NC)',
      'Dakota du Nord (ND)',
      'Ohio (OH)',
      'Oklahoma (OK)',
      'Oregon (OR)',
      'Pennsylvanie (PA)',
      'Rhode Island (RI)',
      'Caroline du Sud (SC)',
      'Dakota du Sud (SD)',
      'Tennessee (TN)',
      'Texas (TX)',
      'Utah (UT)',
      'Vermont (VT)',
      'Virginie (VA)',
      'Washington (WA)',
      'Virginie-Occidentale (WV)',
      'Wisconsin (WI)',
      'Wyoming (WY)',
    ],
    
    // Jurisdictions Europe
    jurisdictionsEuropeList: [
      'Tous les pays',
      'France',
      'Allemagne',
      'Royaume-Uni',
      'Suisse',
      'Belgique',
      'Autre',
    ],
    
    // Jurisdictions Australia
    jurisdictionsAustraliaList: [
      'Tous les états',
      'Nouvelle-Galles du Sud',
      'Victoria',
      'Queensland',
      'Autre',
    ],
  },
};

export type SellerTranslations = typeof SELLER_TRANSLATIONS.en;
